import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import type { StorefrontSearch } from "@/lib/storefront/catalog-filters";
import type { CatalogFacet } from "@/hooks/storefront/use-catalog-view";

interface CatalogFiltersProps {
  search: StorefrontSearch;
  onSearchChange: (patch: Partial<StorefrontSearch>) => void;
  categoryFacets: CatalogFacet[];
  tagFacets: CatalogFacet[];
  priceBounds: { min: number; max: number };
  totalProductCount: number;
}

/** Category, price, tag and availability filters of the catalogue. */
export function CatalogFilters({
  search,
  onSearchChange,
  categoryFacets,
  tagFacets,
  priceBounds,
  totalProductCount,
}: CatalogFiltersProps) {
  const { t } = useTranslation("translation");
  const selectedTags = new Set(search.tags ?? []);

  const toggleTag = (tagId: string) => {
    const next = new Set(selectedTags);
    if (next.has(tagId)) next.delete(tagId);
    else next.add(tagId);
    onSearchChange({ tags: next.size > 0 ? Array.from(next) : undefined });
  };

  return (
    <div className="flex flex-col gap-8">
      <FilterSection title={t("Storefront.Filters.categories")}>
        <ul className="flex flex-col">
          <li>
            <CategoryButton
              label={t("Storefront.Filters.all_products")}
              count={totalProductCount}
              isActive={!search.category}
              onClick={() => onSearchChange({ category: undefined })}
            />
          </li>
          {categoryFacets.map((category) => (
            <li key={category.id}>
              <CategoryButton
                label={category.name}
                count={category.count}
                isActive={search.category === category.id}
                onClick={() => onSearchChange({ category: category.id })}
              />
            </li>
          ))}
        </ul>
      </FilterSection>

      {priceBounds.max > 0 && (
        <FilterSection title={t("Storefront.Filters.price")}>
          <PriceRangeInputs
            // Remount when the URL changes the range from elsewhere ("clear").
            key={`${search.min ?? ""}-${search.max ?? ""}`}
            min={search.min}
            max={search.max}
            bounds={priceBounds}
            onCommit={(min, max) => onSearchChange({ min, max })}
          />
        </FilterSection>
      )}

      {tagFacets.length > 0 && (
        <FilterSection title={t("Storefront.Filters.tags")}>
          <ul className="flex flex-col">
            {tagFacets.map((tag) => (
              <li key={tag.id}>
                <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-2 hover:bg-gray-100">
                  <Checkbox
                    checked={selectedTags.has(tag.id)}
                    onCheckedChange={() => toggleTag(tag.id)}
                  />
                  <span className="flex-1">{tag.name}</span>
                  <span className="text-sm text-gray-500 tabular-nums">
                    {tag.count}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </FilterSection>
      )}

      <FilterSection title={t("Storefront.Filters.availability")}>
        <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-2 hover:bg-gray-100">
          <Checkbox
            checked={!!search.inStock}
            onCheckedChange={(checked) =>
              onSearchChange({ inStock: checked === true ? true : undefined })
            }
          />
          <span>{t("Storefront.Filters.in_stock_only")}</span>
        </label>
      </FilterSection>
    </div>
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="px-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h2>
      {children}
    </section>
  );
}

function CategoryButton({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 rounded-lg px-2 text-start transition-colors hover:bg-gray-100",
        isActive && "bg-gray-100 font-semibold"
      )}
    >
      <span className="min-w-0 truncate">{label}</span>
      <span className="text-sm text-gray-500 tabular-nums">{count}</span>
    </button>
  );
}

function PriceRangeInputs({
  min,
  max,
  bounds,
  onCommit,
}: {
  min?: number;
  max?: number;
  bounds: { min: number; max: number };
  onCommit: (min: number | undefined, max: number | undefined) => void;
}) {
  const { t } = useTranslation("translation");
  const [minText, setMinText] = useState(min != null ? String(min) : "");
  const [maxText, setMaxText] = useState(max != null ? String(max) : "");

  const commit = () => {
    const parse = (text: string) => {
      const value = Number(text.replace(",", "."));
      return text.trim() === "" || !Number.isFinite(value) || value < 0
        ? undefined
        : value;
    };
    let nextMin = parse(minText);
    let nextMax = parse(maxText);
    if (nextMin != null && nextMax != null && nextMin > nextMax) {
      [nextMin, nextMax] = [nextMax, nextMin];
    }
    if (nextMin !== min || nextMax !== max) onCommit(nextMin, nextMax);
  };

  return (
    <form
      className="flex items-center gap-2 px-2"
      onSubmit={(event) => {
        event.preventDefault();
        commit();
      }}
    >
      <PriceInput
        label={t("Storefront.Filters.price_min")}
        placeholder={String(bounds.min)}
        value={minText}
        onChange={setMinText}
        onBlur={commit}
      />
      <span className="text-gray-400">–</span>
      <PriceInput
        label={t("Storefront.Filters.price_max")}
        placeholder={String(bounds.max)}
        value={maxText}
        onChange={setMaxText}
        onBlur={commit}
      />
      {/* Enter in either field applies the range. */}
      <button type="submit" className="sr-only">
        {t("Storefront.Filters.apply")}
      </button>
    </form>
  );
}

function PriceInput({
  label,
  placeholder,
  value,
  onChange,
  onBlur,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  return (
    <label className="flex min-h-11 min-w-0 flex-1 items-center gap-1 rounded-lg border bg-white px-3 focus-within:ring-2 focus-within:ring-black/20">
      <span className="text-gray-500">€</span>
      <span className="sr-only">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        className="w-full min-w-0 bg-transparent py-2 text-base outline-none"
      />
    </label>
  );
}
